/**
 * Browser-local wishlist (localStorage), shared by three elements:
 * - <s-wishlist-toggle data-product-id data-product-title/url/image/price/available>:
 *   heart on product cards; saving stores the card's product data with the id.
 * - <s-wishlist-count>: header badge with the number of saved products.
 * - <s-wishlist-list>: drawer list; clones its <template data-wishlist-row> per
 *   saved product from the stored data (no network requests).
 * Every element stays in sync, including across tabs.
 */
const IDS_KEY = 'velle:wishlist';
const ITEMS_KEY = 'velle:wishlist:items';
const CHANGE_EVENT = 'wishlist:change';

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) ?? fallback : fallback;
  } catch {
    return fallback;
  }
}

function readIds() {
  const value = readJSON(IDS_KEY, []);
  return Array.isArray(value) ? value.map(String) : [];
}

function readItems() {
  const value = readJSON(ITEMS_KEY, {});
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function save(ids, items) {
  try {
    localStorage.setItem(IDS_KEY, JSON.stringify(ids));
    localStorage.setItem(ITEMS_KEY, JSON.stringify(items));
  } catch {
    /* storage unavailable: keep the in-page state only */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function removeFromWishlist(id) {
  const items = readItems();
  delete items[id];
  save(readIds().filter((entry) => entry !== id), items);
}

function onWishlistChange(callback, signal) {
  window.addEventListener(CHANGE_EVENT, callback, { signal });
  window.addEventListener('storage', (event) => {
    if (event.key === IDS_KEY || event.key === ITEMS_KEY) callback();
  }, { signal });
}

class SWishlistToggle extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.querySelector('button')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#toggle();
    }, { signal: this.#ac.signal });
    onWishlistChange(() => this.#sync(), this.#ac.signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #id() {
    return String(this.dataset.productId || '');
  }

  get #item() {
    return {
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '',
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
      available: this.dataset.productAvailable !== 'false',
    };
  }

  #toggle() {
    const id = this.#id;
    if (!id) return;
    const ids = readIds();
    if (ids.includes(id)) {
      removeFromWishlist(id);
    } else {
      const items = readItems();
      items[id] = this.#item;
      save([...ids, id], items);
    }
  }

  #sync() {
    const id = this.#id;
    const saved = readIds().includes(id);
    this.querySelector('button')?.setAttribute('aria-pressed', String(saved));
    // Refresh the stored data (and fill it for items saved before data was stored).
    if (saved && this.dataset.productTitle) {
      const items = readItems();
      const next = this.#item;
      if (JSON.stringify(items[id]) !== JSON.stringify(next)) {
        items[id] = next;
        save(readIds(), items);
      }
    }
  }
}

class SWishlistCount extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    onWishlistChange(() => this.#sync(), this.#ac.signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const items = readItems();
    const count = readIds().filter((id) => items[id]).length;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  }
}

class SWishlistList extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    onWishlistChange(() => this.#render(), this.#ac.signal);
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-wishlist-remove]') : null;
      if (!button) return;
      event.preventDefault();
      removeFromWishlist(String(button.getAttribute('data-wishlist-remove')));
    }, { signal: this.#ac.signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    const list = this.querySelector('[data-wishlist-items]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const template = this.querySelector('template[data-wishlist-row]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const items = readItems();
    const rows = readIds()
      .filter((id) => items[id])
      .map((id) => this.#row(template, id, items[id]));

    list.replaceChildren(...rows);
    if (empty instanceof HTMLElement) empty.hidden = rows.length > 0;
  }

  #row(template, id, item) {
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    for (const link of fragment.querySelectorAll('[data-row-link]')) {
      link.setAttribute('href', item.url || '#');
    }
    const title = fragment.querySelector('[data-row-title]');
    if (title) title.textContent = item.title;
    const price = fragment.querySelector('[data-row-price]');
    if (price) price.textContent = item.price;
    const image = fragment.querySelector('[data-row-image]');
    if (image instanceof HTMLImageElement) {
      if (item.image) image.src = item.image;
      else image.hidden = true;
    }
    const soldOut = fragment.querySelector('[data-row-sold-out]');
    if (soldOut instanceof HTMLElement) soldOut.hidden = item.available !== false;
    fragment.querySelector('[data-wishlist-remove]')?.setAttribute('data-wishlist-remove', id);
    return fragment;
  }
}

if (!customElements.get('s-wishlist-toggle')) customElements.define('s-wishlist-toggle', SWishlistToggle);
if (!customElements.get('s-wishlist-count')) customElements.define('s-wishlist-count', SWishlistCount);
if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', SWishlistList);
